import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, Form, Input } from '@arco-design/web-react';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';

import {
  buildPresetKeySet,
  buildPresetListKeySet,
  resolveDnsListItemKey,
  resolveNamedListItemKey,
} from '../../platform/access-policy/orderListItemLock';
import { isFieldInteractionLocked } from '../../platform/capability-registry/fieldControl';
import { isActionVisible } from '../../platform/capability-registry/resolveFieldMode';
import { useOptionalResourceFormContext } from '../../platform/resource-form';
import { DNS_ACTION_KEYS, DNS_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { DnsBlockProps, DnsPaths } from './types';

interface DnsAddButtonProps {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}

function DnsAddButton({ children, disabled, onClick }: DnsAddButtonProps) {
  return (
    <Button type="dashed" long icon={<IconPlus />} className={styles.addButton} disabled={disabled} onClick={onClick}>
      {children}
    </Button>
  );
}

function resolveDnsOptionKey(row: Record<string, unknown>): string {
  const name = resolveNamedListItemKey(row, 'name');
  const value = String(row.value ?? '').trim();
  if (!name && !value) return '';
  return `${name}\0${value}`;
}

function readListRow(list: unknown, index: number): Record<string, unknown> | undefined {
  if (!Array.isArray(list)) return undefined;
  const row = list[index];
  if (!row || typeof row !== 'object' || Array.isArray(row)) return undefined;
  return row as Record<string, unknown>;
}

/** DNS 配置能力块（nameservers / searches / options） */
export function DnsBlock(props: DnsBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as DnsPaths;
  const { t } = useTranslation();
  const errorMap = options?.errorMap;
  /** preview / 显式 readonly|disabled：整组锁；下单 orderListItemLock 时字段保持 editable，按项锁 */
  const nameserverSectionLocked = isFieldInteractionLocked(DNS_FIELD_KEYS.nameservers, control);
  const optionSectionLocked = isFieldInteractionLocked(DNS_FIELD_KEYS.options, control);
  const searchSectionLocked = isFieldInteractionLocked(DNS_FIELD_KEYS.searches, control);

  const canAddNameserver = isActionVisible(DNS_ACTION_KEYS.nameserverAdd, control) && !nameserverSectionLocked;
  const canRemoveNameserver = isActionVisible(DNS_ACTION_KEYS.nameserverRemove, control) && !nameserverSectionLocked;
  const canAddOption = isActionVisible(DNS_ACTION_KEYS.optionAdd, control) && !optionSectionLocked;
  const canRemoveOption = isActionVisible(DNS_ACTION_KEYS.optionRemove, control) && !optionSectionLocked;
  const canAddSearch = isActionVisible(DNS_ACTION_KEYS.searchAdd, control) && !searchSectionLocked;
  const canRemoveSearch = isActionVisible(DNS_ACTION_KEYS.searchRemove, control) && !searchSectionLocked;

  const nameserversWatch = Form.useWatch(paths.nameservers, form);
  const optionsWatch = Form.useWatch(paths.options, form);
  const searchesWatch = Form.useWatch(paths.searches, form);

  const resourceFormCtx = useOptionalResourceFormContext();
  const presetSnapshot = resourceFormCtx?.presetConfigSnapshot;
  const presetNameserverKeys = useMemo(
    () => buildPresetKeySet(presetSnapshot, paths.nameservers, (item) => resolveDnsListItemKey(item, 'server')),
    [paths.nameservers, presetSnapshot],
  );
  const presetSearchKeys = useMemo(
    () => buildPresetKeySet(presetSnapshot, paths.searches, (item) => resolveDnsListItemKey(item, 'search')),
    [paths.searches, presetSnapshot],
  );
  const presetOptionKeys = useMemo(
    () => buildPresetListKeySet(presetSnapshot, paths.options, resolveDnsOptionKey),
    [paths.options, presetSnapshot],
  );

  const renderNameservers = () => {
    return (
      <div className={styles.section}>
        <div className={styles.panel}>
          <Form.List field={paths.nameservers}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((item, index) => {
                  const current = readListRow(nameserversWatch ?? form.getFieldValue(paths.nameservers), index);
                  const server = String(current?.server ?? '').trim();
                  const inSkuPreset = Boolean(server && presetNameserverKeys.has(server));
                  const rowLocked = nameserverSectionLocked || inSkuPreset;
                  return (
                    <div key={item.key} className={styles.row}>
                      <Form.Item
                        field={`${item.field}.server`}
                        rules={rowLocked ? undefined : [{ required: true, message: t('common:required') }]}
                        className={styles.listItem}
                      >
                        <Input placeholder="server" disabled={rowLocked} readOnly={rowLocked} />
                      </Form.Item>
                      {canRemoveNameserver && !inSkuPreset ? (
                        <Button
                          className={styles.removeButton}
                          icon={<IconDelete />}
                          shape="circle"
                          status="danger"
                          onClick={() => remove(index)}
                        />
                      ) : null}
                    </div>
                  );
                })}
                {canAddNameserver ? <DnsAddButton onClick={() => add()}>{t('common:add') + t('cluster:dns-server')}</DnsAddButton> : null}
              </>
            )}
          </Form.List>
        </div>
        {errorMap?.nameserver ? (
          <div className={styles.errorAlert}>
            <Alert type="error" content={errorMap.nameserver} />
          </div>
        ) : null}
      </div>
    );
  };

  const renderOptions = () => {
    const optionLabel = t('cluster:dns-option');

    return (
      <div className={styles.section}>
        <div className={styles.panel}>
          <Form.List field={paths.options}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((item, index) => {
                  const current = readListRow(optionsWatch ?? form.getFieldValue(paths.options), index);
                  const inSkuPreset = Boolean(current && presetOptionKeys.has(resolveDnsOptionKey(current)));
                  const rowLocked = optionSectionLocked || inSkuPreset;
                  return (
                    <div key={item.key} className={styles.row}>
                      <Form.Item
                        field={`${item.field}.name`}
                        rules={rowLocked ? undefined : [{ required: true, message: t('common:required') }]}
                        className={styles.listItem}
                      >
                        <Input placeholder="name" disabled={rowLocked} readOnly={rowLocked} />
                      </Form.Item>
                      <Form.Item
                        field={`${item.field}.value`}
                        rules={rowLocked ? undefined : [{ required: true, message: t('common:required') }]}
                        className={styles.listItem}
                      >
                        <Input placeholder="value" disabled={rowLocked} readOnly={rowLocked} />
                      </Form.Item>
                      {canRemoveOption && !inSkuPreset ? (
                        <Button
                          className={styles.removeButton}
                          icon={<IconDelete />}
                          shape="circle"
                          status="danger"
                          onClick={() => remove(index)}
                        />
                      ) : null}
                    </div>
                  );
                })}
                {canAddOption ? <DnsAddButton onClick={() => add()}>{t('common:add') + optionLabel}</DnsAddButton> : null}
              </>
            )}
          </Form.List>
        </div>
        {errorMap?.option ? (
          <div className={styles.errorAlert}>
            <Alert type="error" content={errorMap.option} />
          </div>
        ) : null}
      </div>
    );
  };

  const renderSearches = () => {
    return (
      <div className={styles.section}>
        <div className={styles.panel}>
          <Form.List field={paths.searches}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((item, index) => {
                  const current = readListRow(searchesWatch ?? form.getFieldValue(paths.searches), index);
                  const search = String(current?.search ?? '').trim();
                  const inSkuPreset = Boolean(search && presetSearchKeys.has(search));
                  const rowLocked = searchSectionLocked || inSkuPreset;
                  return (
                    <div key={item.key} className={styles.row}>
                      <Form.Item
                        field={`${item.field}.search`}
                        rules={rowLocked ? undefined : [{ required: true, message: t('common:required') }]}
                        className={styles.listItem}
                      >
                        <Input placeholder="search" disabled={rowLocked} readOnly={rowLocked} />
                      </Form.Item>
                      {canRemoveSearch && !inSkuPreset ? (
                        <Button
                          className={styles.removeButton}
                          icon={<IconDelete />}
                          shape="circle"
                          status="danger"
                          onClick={() => remove(index)}
                        />
                      ) : null}
                    </div>
                  );
                })}
                {canAddSearch ? <DnsAddButton onClick={() => add()}>{t('common:add') + t('cluster:dns-search')}</DnsAddButton> : null}
              </>
            )}
          </Form.List>
        </div>
        {errorMap?.searche ? (
          <div className={styles.errorAlert}>
            <Alert type="error" content={errorMap.searche} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.dnsSection}>
      {renderNameservers()}
      {renderOptions()}
      {renderSearches()}
    </div>
  );
}
